/**
 * Malaysian Phone Number Formatter
 * Formats phone numbers in Malaysian format: +60 XX-XXXX XXXX
 */

/**
 * Format Malaysian phone number for display
 * @param {string} phoneNumber - Raw phone number (with or without +60)
 * @returns {string} - Formatted phone number
 */
export function formatMalaysianPhone(phoneNumber) {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  let digits = phoneNumber.replace(/\D/g, '');

  // Remove leading 60 if present (country code)
  if (digits.startsWith('60')) {
    digits = digits.substring(2);
  }

  // Remove leading 0 if present
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  // Format based on number type
  if (digits.length >= 9) {
    // Mobile numbers
    if (digits[0] === '1') {
      const prefix = digits.substring(0, 2);  // 1X

      // Special case: 11 prefix format is 11-XXXX XXXX (4-4 pattern)
      if (prefix === '11' && digits.length >= 10) {
        const middle = digits.substring(2, 6);   // XXXX (4 digits)
        const last = digits.substring(6, 10);    // XXXX (4 digits)
        return `+60 ${prefix}-${middle} ${last}`;
      } else {
        // Other mobile numbers: 1X-XXX XXXX (3-4 pattern)
        const middle = digits.substring(2, 5);   // XXX (3 digits)
        const last = digits.substring(5, 9);     // XXXX (4 digits)
        return `+60 ${prefix}-${middle} ${last}`;
      }
    } else {
      // Landline: +60 X-XXXX XXXX
      const areaCode = digits.substring(0, 1);  // X
      const middle = digits.substring(1, 5);     // XXXX
      const last = digits.substring(5, 9);       // XXXX
      return `+60 ${areaCode}-${middle} ${last}`;
    }
  }

  // Return with +60 prefix for incomplete numbers
  return `+60 ${digits}`;
}

/**
 * Format phone number as user types (for input field)
 * @param {string} value - Current input value
 * @returns {string} - Formatted value for display
 */
export function formatPhoneInput(value) {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Check if it's an 11 prefix
  const is11Prefix = digits.startsWith('11');

  // Format based on length
  if (digits.length === 0) {
    return '';
  } else if (digits.length <= 2) {
    return digits;
  } else if (is11Prefix) {
    // 11 prefix format: 11-XXXX XXXX (4-4 pattern)
    if (digits.length <= 6) {
      return `${digits.substring(0, 2)}-${digits.substring(2)}`;
    } else {
      return `${digits.substring(0, 2)}-${digits.substring(2, 6)} ${digits.substring(6, 10)}`;
    }
  } else {
    // Other prefixes format: 1X-XXX XXXX (3-4 pattern)
    if (digits.length <= 5) {
      return `${digits.substring(0, 2)}-${digits.substring(2)}`;
    } else {
      return `${digits.substring(0, 2)}-${digits.substring(2, 5)} ${digits.substring(5, 9)}`;
    }
  }
}

/**
 * Remove formatting from phone number (for storage)
 * @param {string} formattedNumber - Formatted phone number
 * @returns {string} - Raw number with +60 prefix
 */
export function unformatPhone(formattedNumber) {
  if (!formattedNumber) return '';

  // Remove all non-digit characters except leading +
  let digits = formattedNumber.replace(/[^\d+]/g, '');

  // If it doesn't start with +60, add it
  if (!digits.startsWith('+60')) {
    // Remove any existing + or leading zeros
    digits = digits.replace(/^[\+0]+/, '');
    digits = `+60${digits}`;
  }

  return digits;
}

/**
 * Validate Malaysian phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid
 */
export function isValidMalaysianPhone(phoneNumber) {
  const digits = phoneNumber.replace(/\D/g, '');

  // Remove country code if present
  let localDigits = digits;
  if (localDigits.startsWith('60')) {
    localDigits = localDigits.substring(2);
  }
  if (localDigits.startsWith('0')) {
    localDigits = localDigits.substring(1);
  }

  // Malaysian phone numbers validation
  // Mobile starting with 11: 10 digits (11-XXXX XXXX)
  // Other mobile starting with 1: 9 digits (1X-XXX XXXX)
  // Landline: starts with 2-9 (9 digits)
  if (localDigits.startsWith('11')) {
    return localDigits.length === 10;
  } else if (localDigits.startsWith('1')) {
    return localDigits.length === 9;
  } else {
    return localDigits.length === 9;
  }
}
